import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { writeFetch, getSession } from '../../../api';
import { secureUpload, formatFileSize } from '../../../services/s3UploadService';

const {
  FiPlus, FiTrash2, FiImage, FiStar, FiArrowUp, FiArrowDown,
  FiCheck, FiX, FiEdit2, FiUpload, FiLink, FiVideo, FiYoutube,
  FiFilm, FiAlertTriangle, FiLoader,
} = FiIcons;

const IMG_TABLE   = 'product_images_1776542000';
const VIDEO_TABLE = 'product_media_1776543000';
const MAX_VIDEO_BYTES = 50 * 1024 * 1024; // 50 MB proxy for ~1 min video
const MAX_VIDEO_SECS  = 60;

// ── helpers ───────────────────────────────────────────────────────────────────
const isYouTube = (url) => /youtu(be\.com|\.be)/i.test(url);
const isVimeo   = (url) => /vimeo\.com/i.test(url);

function getVideoEmbedUrl(url) {
  if (!url) return null;
  const ytMatch = url.match(/(?:v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url; // direct
}

function getVideoPlatformLabel(url) {
  if (isYouTube(url)) return 'YouTube';
  if (isVimeo(url))   return 'Vimeo';
  return 'Uploaded';
}

// ── Tab button ────────────────────────────────────────────────────────────────
const TabBtn = ({ active, onClick, icon, label }) => (
  <button onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 transition-all ${
      active
        ? 'bg-accent-navy border-accent-navy text-white'
        : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
    }`}>
    <SafeIcon icon={icon} className="w-3.5 h-3.5" /> {label}
  </button>
);

// ── Upload progress bar ───────────────────────────────────────────────────────
const UploadBar = ({ pct }) => (
  <div className="w-full bg-brand-light rounded-full h-2 overflow-hidden mt-2">
    <motion.div className="h-full bg-accent-gold rounded-full"
      initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.2 }} />
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
//  IMAGE SECTION
// ══════════════════════════════════════════════════════════════════════════════

// ── Add Image Modal (URL + File upload) ───────────────────────────────────────
const AddImageModal = ({ onClose, onAdd }) => {
  const user = getSession();
  const fileRef  = useRef();
  const [mode, setMode]       = useState('url'); // 'url' | 'file'
  const [url, setUrl]         = useState('');
  const [alt, setAlt]         = useState('');
  const [preview, setPreview] = useState('');
  const [error, setError]     = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [fileInfo, setFileInfo]   = useState(null);

  const handleUrlPreview = () => {
    if (!url.trim()) return setError('Enter an image URL');
    setPreview(url.trim()); setError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return setError('Please select an image file.');
    setFileInfo(file);
    setPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleAdd = async () => {
    if (mode === 'url') {
      if (!url.trim()) return setError('URL is required');
      onAdd({ image_url: url.trim(), alt_text: alt.trim() });
      onClose();
    } else {
      if (!fileInfo) return setError('Please select a file');
      setUploading(true);
      try {
        const { s3Url } = await secureUpload({
          file: fileInfo,
          ownerId: user?.id || 'anonymous',
          category: 'product-images',
          onProgress: setUploadPct,
        });
        onAdd({ image_url: s3Url, alt_text: alt.trim() || fileInfo.name });
        onClose();
      } catch (err) {
        setError(err.message || 'Upload failed');
      }
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-[32px] shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-6 border-b border-brand-light flex justify-between items-center">
          <h3 className="text-lg font-serif text-accent-navy">Add Product Image</h3>
          <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full">
            <SafeIcon icon={FiX} className="w-4 h-4 text-text-muted" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {/* Mode tabs */}
          <div className="flex gap-2">
            <TabBtn active={mode === 'url'}  onClick={() => { setMode('url');  setPreview(''); setError(''); }} icon={FiLink}   label="Image URL" />
            <TabBtn active={mode === 'file'} onClick={() => { setMode('file'); setPreview(''); setError(''); }} icon={FiUpload} label="Upload File" />
          </div>

          {mode === 'url' ? (
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Image URL *</label>
              <div className="flex gap-2">
                <input value={url} onChange={e => { setUrl(e.target.value); setError(''); }}
                  placeholder="https://images.unsplash.com/..."
                  className="flex-1 px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm" />
                <button onClick={handleUrlPreview}
                  className="px-4 py-3 bg-accent-navy text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all">
                  Preview
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Choose Image File</label>
              <button onClick={() => fileRef.current?.click()}
                className="w-full py-6 border-2 border-dashed border-brand-dark rounded-xl flex flex-col items-center gap-2 text-text-muted hover:border-accent-gold hover:text-accent-navy transition-all">
                <SafeIcon icon={FiUpload} className="w-7 h-7" />
                <span className="text-[10px] font-black uppercase tracking-widest">
                  {fileInfo ? fileInfo.name : 'Click to select image'}
                </span>
                {fileInfo && <span className="text-[9px] opacity-60">{formatFileSize(fileInfo.size)}</span>}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
            </div>
          )}

          {preview && (
            <div className="relative rounded-2xl overflow-hidden border border-brand-dark aspect-video bg-brand-light">
              <img src={preview} alt="preview" className="w-full h-full object-cover"
                onError={() => setError('Could not load image from this URL')} />
            </div>
          )}

          {uploading && <UploadBar pct={uploadPct} />}
          {error && <p className="text-red-500 text-xs">{error}</p>}

          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Alt Text (optional)</label>
            <input value={alt} onChange={e => setAlt(e.target.value)}
              placeholder="Describe this image..."
              className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm" />
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button onClick={onClose} disabled={uploading}
            className="flex-1 py-3 border-2 border-brand-dark rounded-xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-navy transition-all disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleAdd} disabled={uploading || (mode === 'url' ? !url.trim() : !fileInfo)}
            className="flex-1 py-3 bg-accent-gold text-accent-navy rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy hover:text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2">
            {uploading
              ? <><SafeIcon icon={FiLoader} className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
              : <><SafeIcon icon={FiCheck} className="w-3.5 h-3.5" /> Add Image</>}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ── Image Thumbnail Card ───────────────────────────────────────────────────────
const ImageCard = ({ img, index, total, onDelete, onSetPrimary, onMoveUp, onMoveDown, onEditAlt }) => {
  const [editingAlt, setEditingAlt] = useState(false);
  const [alt, setAlt] = useState(img.alt_text || '');
  const handleSaveAlt = () => { onEditAlt(img.id, alt); setEditingAlt(false); };

  return (
    <motion.div layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`relative rounded-2xl overflow-hidden border-2 transition-all group ${
        img.is_primary ? 'border-accent-gold shadow-lg' : 'border-brand-dark hover:border-accent-gold/50'
      }`}>
      <div className="aspect-square bg-brand-light relative">
        <img src={img.image_url} alt={img.alt_text}
          className="w-full h-full object-cover"
          onError={e => { e.target.src = 'https://images.unsplash.com/photo-1563241527-3004b7be0ffd?q=80&w=400'; }} />
        {img.is_primary === 1 && (
          <div className="absolute top-2 left-2 bg-accent-gold text-white text-[9px] font-black px-2 py-0.5 rounded-full flex items-center gap-1">
            <SafeIcon icon={FiStar} className="w-2.5 h-2.5" /> Primary
          </div>
        )}
        <div className="absolute top-2 right-2 bg-black/50 text-white text-[9px] font-black w-5 h-5 rounded-full flex items-center justify-center backdrop-blur-sm">
          {index + 1}
        </div>
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-2">
          {img.is_primary !== 1 && (
            <button onClick={() => onSetPrimary(img.id)} title="Set primary"
              className="p-2 bg-accent-gold rounded-xl text-white hover:scale-110 transition-transform">
              <SafeIcon icon={FiStar} className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={() => setEditingAlt(true)} title="Edit alt"
            className="p-2 bg-white/20 rounded-xl text-white hover:scale-110 transition-transform">
            <SafeIcon icon={FiEdit2} className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(img.id)} title="Delete"
            className="p-2 bg-red-500/80 rounded-xl text-white hover:scale-110 transition-transform">
            <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="flex border-t border-brand-dark">
        <button onClick={() => onMoveUp(index)} disabled={index === 0}
          className="flex-1 py-1.5 text-text-muted hover:bg-brand-light disabled:opacity-30 transition-all flex items-center justify-center">
          <SafeIcon icon={FiArrowUp} className="w-3 h-3" />
        </button>
        <div className="w-px bg-brand-dark" />
        <button onClick={() => onMoveDown(index)} disabled={index === total - 1}
          className="flex-1 py-1.5 text-text-muted hover:bg-brand-light disabled:opacity-30 transition-all flex items-center justify-center">
          <SafeIcon icon={FiArrowDown} className="w-3 h-3" />
        </button>
      </div>
      {editingAlt && (
        <div className="absolute inset-0 bg-white p-3 flex flex-col gap-2 z-10">
          <label className="text-[9px] font-black uppercase tracking-widest text-accent-navy">Alt Text</label>
          <input value={alt} onChange={e => setAlt(e.target.value)}
            className="flex-1 px-2 py-1 bg-brand-light border border-brand-dark rounded-lg text-xs focus:border-accent-gold outline-none" />
          <div className="flex gap-1">
            <button onClick={handleSaveAlt}
              className="flex-1 py-1 bg-accent-navy text-white rounded-lg text-[9px] font-black uppercase tracking-widest">Save</button>
            <button onClick={() => setEditingAlt(false)}
              className="flex-1 py-1 border border-brand-dark rounded-lg text-[9px] font-black uppercase tracking-widest text-text-muted">Cancel</button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  VIDEO SECTION
// ══════════════════════════════════════════════════════════════════════════════
const VideoSection = ({ productId }) => {
  const user = getSession();
  const fileRef = useRef();
  const videoPreviewRef = useRef();

  const [videos, setVideos]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [mode, setMode]           = useState('url'); // 'url' | 'file'
  const [videoUrl, setVideoUrl]   = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [error, setError]         = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [fileInfo, setFileInfo]   = useState(null);
  const [fileObjUrl, setFileObjUrl] = useState('');
  const [durationOk, setDurationOk] = useState(null); // null | true | false

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const res  = await writeFetch(`read/${VIDEO_TABLE}?product_id=${productId}&limit=10`);
      const data = await res.json();
      if (data.status === 'success') setVideos(data.data || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  // Check video duration when file chosen
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) return setError('Please select a video file (MP4, WebM, etc.)');
    if (file.size > MAX_VIDEO_BYTES) return setError(`File too large. Max ~50 MB for a 1-minute clip.`);
    const objUrl = URL.createObjectURL(file);
    setFileInfo(file);
    setFileObjUrl(objUrl);
    setDurationOk(null);
    setError('');
  };

  const handleVideoMetadata = () => {
    const vid = videoPreviewRef.current;
    if (!vid) return;
    if (vid.duration > MAX_VIDEO_SECS) {
      setDurationOk(false);
      setError(`Video is ${Math.round(vid.duration)}s — max is 60 seconds.`);
    } else {
      setDurationOk(true);
      setError('');
    }
  };

  const handleAddVideo = async () => {
    if (mode === 'url') {
      if (!videoUrl.trim()) return setError('Enter a video URL');
      const now = new Date().toISOString();
      try {
        await writeFetch(`create/${VIDEO_TABLE}`, {
          method: 'POST',
          body: JSON.stringify({
            id: crypto.randomUUID(), product_id: productId,
            owner_id: user?.id || 'anonymous',
            media_type: 'video', video_source: isYouTube(videoUrl) ? 'youtube' : isVimeo(videoUrl) ? 'vimeo' : 'url',
            media_url: videoUrl.trim(), title: videoTitle.trim() || 'Product Video',
            created_at: now, updated_at: now,
          }),
        });
        setVideoUrl(''); setVideoTitle(''); setError('');
        await load();
      } catch (err) { setError(err.message || 'Failed to save video'); }
    } else {
      if (!fileInfo) return setError('Select a video file');
      if (durationOk === false) return setError('Video exceeds 60 seconds.');
      setUploading(true);
      try {
        const { s3Url, s3Key } = await secureUpload({
          file: fileInfo,
          ownerId: user?.id || 'anonymous',
          category: 'product-videos',
          onProgress: setUploadPct,
        });
        const now = new Date().toISOString();
        await writeFetch(`create/${VIDEO_TABLE}`, {
          method: 'POST',
          body: JSON.stringify({
            id: crypto.randomUUID(), product_id: productId,
            owner_id: user?.id || 'anonymous',
            media_type: 'video', video_source: 'upload',
            media_url: s3Url, s3_key: s3Key,
            title: videoTitle.trim() || fileInfo.name,
            file_name: fileInfo.name, file_size: fileInfo.size,
            created_at: now, updated_at: now,
          }),
        });
        setFileInfo(null); setFileObjUrl(''); setVideoTitle(''); setDurationOk(null);
        await load();
      } catch (err) { setError(err.message || 'Upload failed'); }
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await writeFetch(`delete/${VIDEO_TABLE}/${id}`, { method: 'DELETE' });
      setVideos(v => v.filter(x => x.id !== id));
    } catch { /* silent */ }
  };

  return (
    <div className="space-y-4 mt-6 pt-6 border-t border-brand-dark">
      <div className="flex items-center gap-2">
        <SafeIcon icon={FiVideo} className="w-4 h-4 text-accent-navy" />
        <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">
          Product Video ({videos.length}/1)
        </p>
        <span className="text-[9px] text-text-muted font-light">Max 1 video · 60 seconds</span>
      </div>

      {/* Existing videos */}
      {loading ? (
        <div className="h-20 bg-brand-light rounded-2xl animate-pulse" />
      ) : videos.length > 0 ? (
        <div className="space-y-3">
          {videos.map(v => (
            <div key={v.id} className="flex items-center gap-3 p-4 bg-brand-light rounded-2xl border border-brand-dark">
              <div className="w-14 h-10 bg-black rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center">
                {v.video_source === 'upload'
                  ? <SafeIcon icon={FiFilm} className="w-5 h-5 text-white/60" />
                  : <SafeIcon icon={FiYoutube} className="w-5 h-5 text-red-400" />
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-accent-navy truncate">{v.title || 'Product Video'}</p>
                <p className="text-[9px] text-text-muted uppercase tracking-widest">{getVideoPlatformLabel(v.media_url)}</p>
              </div>
              <button onClick={() => handleDelete(v.id)}
                className="p-2 text-text-muted hover:text-red-500 transition-colors flex-shrink-0">
                <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {/* Add video form (only if no video yet) */}
      {videos.length === 0 && (
        <div className="space-y-3 p-4 bg-brand-light/50 rounded-2xl border border-brand-dark">
          <div className="flex gap-2">
            <TabBtn active={mode === 'url'}  onClick={() => { setMode('url');  setError(''); setFileInfo(null); setFileObjUrl(''); }} icon={FiLink}   label="Video URL" />
            <TabBtn active={mode === 'file'} onClick={() => { setMode('file'); setError(''); setVideoUrl('');   }} icon={FiUpload} label="Upload Video" />
          </div>

          {mode === 'url' ? (
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">
                YouTube / Vimeo / Direct URL
              </label>
              <input value={videoUrl} onChange={e => { setVideoUrl(e.target.value); setError(''); }}
                placeholder="https://youtube.com/watch?v=... or direct MP4 URL"
                className="w-full px-4 py-3 bg-white border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm" />
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">
                Video File (MP4 / WebM · max 60 seconds)
              </label>
              <button onClick={() => fileRef.current?.click()}
                className={`w-full py-5 border-2 border-dashed rounded-xl flex flex-col items-center gap-2 transition-all ${
                  durationOk === false
                    ? 'border-red-400 text-red-500'
                    : durationOk === true
                    ? 'border-green-400 text-green-600'
                    : 'border-brand-dark text-text-muted hover:border-accent-gold hover:text-accent-navy'
                }`}>
                <SafeIcon icon={FiVideo} className="w-7 h-7" />
                <span className="text-[10px] font-black uppercase tracking-widest">
                  {fileInfo ? fileInfo.name : 'Click to select video'}
                </span>
                {fileInfo && (
                  <span className="text-[9px] opacity-70">
                    {formatFileSize(fileInfo.size)} · {durationOk === true ? '✅ Duration OK' : durationOk === false ? '❌ Too long' : 'Checking…'}
                  </span>
                )}
              </button>
              <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={handleFileChange} />

              {/* Hidden video element to check duration */}
              {fileObjUrl && (
                <video ref={videoPreviewRef} src={fileObjUrl} className="hidden"
                  onLoadedMetadata={handleVideoMetadata} preload="metadata" />
              )}

              {durationOk === false && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                  <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-xs text-red-600">Video must be 60 seconds or less. Please trim and re-upload.</p>
                </div>
              )}
            </div>
          )}

          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Title (optional)</label>
            <input value={videoTitle} onChange={e => setVideoTitle(e.target.value)}
              placeholder="e.g. Product Demo"
              className="w-full px-4 py-3 bg-white border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm" />
          </div>

          {uploading && <UploadBar pct={uploadPct} />}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
              <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}

          <button onClick={handleAddVideo}
            disabled={uploading || (mode === 'file' && (!fileInfo || durationOk === false))}
            className="w-full py-3 bg-accent-navy text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all disabled:opacity-50 flex items-center justify-center gap-2">
            {uploading
              ? <><SafeIcon icon={FiLoader} className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
              : <><SafeIcon icon={FiPlus} className="w-3.5 h-3.5" /> Add Video</>}
          </button>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  MAIN EXPORT
// ══════════════════════════════════════════════════════════════════════════════
const ProductImageManager = ({ productId, productName }) => {
  const user = getSession();
  const [images, setImages]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const res  = await writeFetch(`read/${IMG_TABLE}?product_id=${productId}&sort=sort_order&order=asc&limit=50`);
      const data = await res.json();
      if (data.status === 'success') setImages(data.data || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async ({ image_url, alt_text }) => {
    setSaving(true);
    const now = new Date().toISOString();
    const isPrimary = images.length === 0 ? 1 : 0;
    try {
      await writeFetch(`create/${IMG_TABLE}`, {
        method: 'POST',
        body: JSON.stringify({
          id: crypto.randomUUID(), product_id: productId,
          owner_id: user?.id || 'anonymous',
          image_url, alt_text, sort_order: images.length,
          is_primary: isPrimary, created_at: now, updated_at: now,
        }),
      });
      await load();
    } catch { /* silent */ }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const wasDeleted = images.find(i => i.id === id);
    try {
      await writeFetch(`delete/${IMG_TABLE}/${id}`, { method: 'DELETE' });
      const remaining = images.filter(i => i.id !== id);
      if (wasDeleted?.is_primary && remaining.length > 0) {
        await writeFetch(`update/${IMG_TABLE}/${remaining[0].id}`, {
          method: 'PUT', body: JSON.stringify({ is_primary: 1 }),
        });
      }
      await load();
    } catch { /* silent */ }
  };

  const handleSetPrimary = async (id) => {
    setSaving(true);
    try {
      await Promise.allSettled(
        images.filter(i => i.is_primary).map(i =>
          writeFetch(`update/${IMG_TABLE}/${i.id}`, { method: 'PUT', body: JSON.stringify({ is_primary: 0 }) })
        )
      );
      await writeFetch(`update/${IMG_TABLE}/${id}`, { method: 'PUT', body: JSON.stringify({ is_primary: 1 }) });
      await load();
    } catch { /* silent */ }
    setSaving(false);
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const r = [...images];
    [r[index], r[index - 1]] = [r[index - 1], r[index]];
    setImages(r);
    await Promise.allSettled(r.map((img, i) =>
      writeFetch(`update/${IMG_TABLE}/${img.id}`, { method: 'PUT', body: JSON.stringify({ sort_order: i }) })
    ));
  };

  const handleMoveDown = async (index) => {
    if (index === images.length - 1) return;
    const r = [...images];
    [r[index], r[index + 1]] = [r[index + 1], r[index]];
    setImages(r);
    await Promise.allSettled(r.map((img, i) =>
      writeFetch(`update/${IMG_TABLE}/${img.id}`, { method: 'PUT', body: JSON.stringify({ sort_order: i }) })
    ));
  };

  const handleEditAlt = async (id, alt_text) => {
    await writeFetch(`update/${IMG_TABLE}/${id}`, { method: 'PUT', body: JSON.stringify({ alt_text }) });
    setImages(prev => prev.map(i => i.id === id ? { ...i, alt_text } : i));
  };

  return (
    <div className="space-y-4">
      {/* ── Images ── */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy flex items-center gap-1.5">
            <SafeIcon icon={FiImage} className="w-3.5 h-3.5" /> Product Gallery ({images.length})
          </p>
          <p className="text-[9px] text-text-muted mt-0.5">Upload files or paste URLs. ⭐ = primary image shown in listings.</p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-accent-navy text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all">
          <SafeIcon icon={FiPlus} className="w-3 h-3" /> Add Image
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => <div key={i} className="aspect-square bg-brand-light rounded-2xl animate-pulse" />)}
        </div>
      ) : images.length === 0 ? (
        <button onClick={() => setShowAdd(true)}
          className="w-full py-8 border-2 border-dashed border-brand-dark rounded-2xl flex flex-col items-center gap-2 text-text-muted hover:border-accent-gold hover:text-accent-navy transition-all">
          <SafeIcon icon={FiImage} className="w-8 h-8" />
          <span className="text-[10px] font-black uppercase tracking-widest">Add your first image</span>
          <span className="text-[9px]">Upload from computer or paste a URL</span>
        </button>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          <AnimatePresence>
            {images.map((img, i) => (
              <ImageCard key={img.id} img={img} index={i} total={images.length}
                onDelete={handleDelete} onSetPrimary={handleSetPrimary}
                onMoveUp={handleMoveUp} onMoveDown={handleMoveDown}
                onEditAlt={handleEditAlt} />
            ))}
          </AnimatePresence>
          <button onClick={() => setShowAdd(true)}
            className="aspect-square border-2 border-dashed border-brand-dark rounded-2xl flex flex-col items-center justify-center gap-2 text-text-muted hover:border-accent-gold hover:text-accent-navy transition-all">
            <SafeIcon icon={FiPlus} className="w-6 h-6" />
            <span className="text-[9px] font-black uppercase tracking-widest">Add</span>
          </button>
        </div>
      )}

      {/* ── Video ── */}
      {productId && <VideoSection productId={productId} />}

      <AnimatePresence>
        {showAdd && <AddImageModal onClose={() => setShowAdd(false)} onAdd={handleAdd} />}
      </AnimatePresence>
    </div>
  );
};

export default ProductImageManager;